import { useMutation } from '@tanstack/react-query';
import { type FC, useCallback, useEffect, useState } from 'react';
import {
  useDataProvider,
  useListContext,
  useNotify,
  useTranslate,
  useUnselectAll,
} from 'ra-core';
import { Ban, Check, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { type AdminDataProvider } from '../../api/adminDataProvider';
import { ResourcesValue } from '../resources';
import { type User } from './types';

export const BulkUserDisableAction: FC = () => {
  const { data, selectedIds, refetch } = useListContext<User>();
  const [selected, setSelected] = useState<
    Array<{ id: number; isDisabled: boolean }>
  >([]);
  const dataProvider = useDataProvider<AdminDataProvider>();
  const translate = useTranslate();
  const notify = useNotify();
  const unselectAll = useUnselectAll(ResourcesValue.USERS);

  // TODO: This needs a refactor since including `selectedIds` in the dependency array causes infinite loop and without it, the values of selectedIds might be outdated
  useEffect(() => {
    const updateSelected = [...selected];
    if (!data) {
      setSelected([]);
      return;
    }

    data
      .filter((u) => selectedIds.includes(u.id))
      .forEach((user) => {
        const existingSelection = updateSelected.find(
          (sel) => sel.id === user.id
        );
        const newUserSelection = {
          id: user.id,
          isDisabled: !!user.disabledFrom,
        };

        if (existingSelection)
          updateSelected[updateSelected.indexOf(existingSelection)] =
            newUserSelection;
        else updateSelected.push(newUserSelection);
      });

    const filteredSelected = updateSelected.filter((sel) =>
      selectedIds.includes(sel.id)
    );

    setSelected(filteredSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, data]);

  const shouldDisableUsers = selected.some((item) => !item.isDisabled);

  const setEnabledUsers = useMutation({
    mutationFn: dataProvider.setUsersEnabled,
    onSuccess: (e) => {
      if (e.failedUserIds.length === 0) {
        notify(
          translate('moonstar.bulk_disable.success', {
            count: e.successfulUserIds.length,
          }),
          { type: 'success' }
        );
      } else {
        notify(
          translate('moonstar.bulk_disable.partial_success', {
            total_success: e.successfulUserIds.length,
            total_failed: e.failedUserIds.length,
          }),
          { type: 'warning' }
        );
      }
    },
    onError: () => {
      notify(translate('moonstar.bulk_disable.error'), {
        type: 'error',
        autoHideDuration: 5000,
      });
    },
    onSettled: () => {
      void refetch();
      unselectAll();
    },
  });

  const enableUsers = useCallback(() => {
    setEnabledUsers.mutate({ userIds: selectedIds, isEnabled: true });
  }, [selectedIds, setEnabledUsers]);

  const disableUsers = useCallback(() => {
    setEnabledUsers.mutate({ userIds: selectedIds, isEnabled: false });
  }, [selectedIds, setEnabledUsers]);

  // Keep the button (disabled) while pending, so the toolbar doesn't jump and the action stays labelled
  const pendingIcon = <Loader2 className="animate-spin" aria-hidden />;

  if (shouldDisableUsers) {
    return (
      <Button
        onClick={disableUsers}
        variant="destructive"
        disabled={setEnabledUsers.isPending}
      >
        {setEnabledUsers.isPending ? pendingIcon : <Ban />}
        {translate('moonstar.user.disable.disable')}
      </Button>
    );
  } else {
    return (
      <Button
        onClick={enableUsers}
        variant="outline"
        disabled={setEnabledUsers.isPending}
      >
        {setEnabledUsers.isPending ? pendingIcon : <Check />}
        {translate('moonstar.user.disable.enable')}
      </Button>
    );
  }
};
